/** Based on Mass PFML: https://github.com/navapbc/archive-massgov-pfml/blob/main/portal/src/components/mayflower/BrandBanner.tsx */

import classnames from 'classnames';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import MayflowerIcon from './MayflowerIcon';

/**
 * A thin banner attached to the top of all pages of the site for branding and
 * assurance purposes. It assures visitors that they are using an official and
 * secure government website.
 *
 * Requires Mayflower styles used in app.scss:
 * - "~@massds/mayflower-assets/scss/01-atoms/button-with-icon"
 * - "~@massds/mayflower-assets/scss/02-molecules/brand-banner"
 *
 * Behavior ported from "@massds/mayflower-assets@11.24.0/js/brand-banner.js"
 */
const BrandBanner = () => {
  const t = useTranslations('components.Header');

  const iconProps = {
    fill: 'currentColor',
    height: '30',
    width: '30',
  };
  const iconPropsSmall = {
    fill: 'currentColor',
    height: '12',
    width: '12',
  };
  const iconPropsChevron = {
    fill: 'currentColor',
    height: '1em',
    width: '1em',
  };

  const [isExpanded, setExpanded] = useState(false);
  const handleClick = () => {
    setExpanded(!isExpanded);
    const banner = document.getElementById('ma__brand-banner-content');

    if (!isExpanded) {
      banner?.focus();
    }
  };
  const brandBannerExpansionClasses = classnames('ma__brand-banner-expansion', {
    'ma__brand-banner-expansion--expanded': isExpanded,
  });

  return (
    <div className="ma__brand-banner ma__brand-banner--c-primary-bg-light" data-testid="mayflower_brand_banner">
      <button
        className="ma__brand-banner-container"
        aria-controls="ma__brand-banner-content"
        aria-expanded={isExpanded}
        onClick={handleClick}
        type="button"
      >
        <img
          className="ma__brand-banner-logo"
          src={`${process.env.NEXT_PUBLIC_BASE_PATH}/img/logo2.png`}
          alt={t('seal_alt')}
        />
        <span className="ma__brand-banner-text">
          <span>{t('button_message')}</span>
          <span>&nbsp;&nbsp;&nbsp;</span>
          <span className="ma__brand-banner-button ma__button-icon ma__icon-small ma__button-icon--quaternary ma__button-icon--c-primary">
            <span>{t('button_call_to_action')}</span>
            <MayflowerIcon name="chevron" {...iconPropsChevron} />
          </span>
        </span>
      </button>
      <ul className={brandBannerExpansionClasses} id="ma__brand-banner-content">
        <li className="ma__brand-banner-expansion-item">
          <span className="text-primary">
            <MayflowerIcon name="building" {...iconProps} />
          </span>
          <div className="ma__brand-banner-expansion-item-content">
            {t.rich('official_message', {
              p: (content) => <p>{content}</p>,
            })}
          </div>
        </li>
        <li className="ma__brand-banner-expansion-item">
          <span className="text-secondary">
            <MayflowerIcon name="lock" {...iconProps} />
          </span>
          <div className="ma__brand-banner-expansion-item-content">
            {t.rich('security_message', {
              p: (content) => <p>{content}</p>,
              lock: () => <MayflowerIcon name="lock" {...iconPropsSmall} />,
            })}
          </div>
        </li>
      </ul>
    </div>
  );
};

export default BrandBanner;
